import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useHelpCategories, useHelpArticles, useHelpSearch } from "@/hooks/useHelpCenter";
import { Search, BookOpen, FileText, ChevronRight } from "lucide-react";

export function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: categories, isLoading: categoriesLoading } = useHelpCategories();
  const { data: articles, isLoading: articlesLoading } = useHelpArticles();
  const {
    data: searchResults,
    isLoading: searchLoading,
    isError: searchError,
  } = useHelpSearch(searchQuery);
  // The search RPC returns category_id; links need the category slug.
  const categorySlugById = new Map(categories?.map((c) => [c.id, c.slug]));

  const isSearching = searchQuery.length > 0;

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Help Center</h1>
        <p className="text-muted-foreground">
          Find answers to common questions and learn how to use the platform
        </p>
      </div>

      <div className="relative mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search for help articles..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {isSearching ? (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            Search Results {searchResults?.length ? `(${searchResults.length})` : ""}
          </h2>
          {searchLoading || categoriesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : searchError ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Search is unavailable right now. Please try again in a moment.
              </CardContent>
            </Card>
          ) : !searchResults?.length ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No articles found matching "{searchQuery}"
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {searchResults?.map((result) => (
                <Link
                  key={result.id}
                  to={`/help/${categorySlugById.get(result.category_id)}/${result.slug}`}
                  className="block"
                >
                  <Card className="hover:bg-accent transition-colors">
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium">{result.title}</h3>
                          {result.excerpt && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {result.excerpt}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 mb-8">
            {categoriesLoading ? (
              [1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))
            ) : categories?.length === 0 ? (
              <Card className="col-span-2">
                <CardContent className="py-8 text-center text-muted-foreground">
                  <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No categories available yet
                </CardContent>
              </Card>
            ) : (
              categories?.map((category) => (
                <Link key={category.id} to={`/help/${category.slug}`}>
                  <Card className="h-full hover:bg-accent transition-colors cursor-pointer">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5" />
                        {category.name}
                      </CardTitle>
                      {category.description && (
                        <CardDescription>{category.description}</CardDescription>
                      )}
                    </CardHeader>
                  </Card>
                </Link>
              ))
            )}
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">Recent Articles</h2>
            {articlesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : articles?.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No articles available yet
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {articles?.slice(0, 5).map((article) => (
                  <Link
                    key={article.id}
                    to={`/help/${(article as any).help_categories?.slug || 'general'}/${article.slug}`}
                    className="block"
                  >
                    <Card className="hover:bg-accent transition-colors">
                      <CardContent className="py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span>{article.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {(article as any).help_categories?.slug && (
                            <Badge variant="secondary" className="text-xs">
                              {(article as any).help_categories.slug}
                            </Badge>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
